import { useState, useRef, useEffect, useLayoutEffect, createRef, useCallback } from "react";
import TooltippedElement from "components/TooltippedElement";

interface ToggleSwitchOption {
  label: string;
  value: string;
  tooltip?: string;
}

interface ToggleSwitchProps {
  options: ToggleSwitchOption[];
  defaultValue?: string | null;
  value?: string | null;
  onChange?: (value: string) => void;
  className?: string;
}

const ToggleSwitch = ({ 
  options = [],
  defaultValue = null,
  value = null,
  onChange = () => {},
  className = ""
}: ToggleSwitchProps) => {
  const [selected, setSelected] = useState<string>(defaultValue || options[0]?.value || "");
  const [highlighterStyle, setHighlighterStyle] = useState({
    width: "0px",
    transform: "translateX(0px)",
  });
  
  // Container ref to handle relative positioning correctly
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Create refs for each option - only recreate when options length changes
  const refs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  if (refs.current.length !== options.length) {
    refs.current = options.map(() => createRef<HTMLButtonElement>());
  }
  
  // Combine both value and defaultValue effects into one
  useEffect(() => {
    if (value !== null && value !== undefined) {
      setSelected(value);
    } else if (defaultValue !== null && defaultValue !== undefined) {
      setSelected(defaultValue);
    }
  }, [value, defaultValue]);
  
  // Centralized function to update highlighter position
  const updateHighlighterPosition = useCallback(() => {
    // Use requestAnimationFrame to ensure measurement happens after layout
    requestAnimationFrame(() => {
      const selectedIndex = options.findIndex(option => option.value === selected);
      if (selectedIndex >= 0 && refs.current[selectedIndex]?.current) {
        const targetRef = refs.current[selectedIndex];
        if (targetRef.current && containerRef.current) {
          // Force a reflow to ensure accurate measurements for text with spaces
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          targetRef.current.offsetHeight; // This forces a reflow
          
          const width = targetRef.current.offsetWidth;
          const left = targetRef.current.offsetLeft;
          
          // Only update if we have valid dimensions
          if (width > 0) {
            setHighlighterStyle({
              width: `${width}px`,
              transform: `translateX(${left}px)`,
            });
          }
        }
      }
    });
  }, [selected, options]);
  
  // Use ResizeObserver to watch for button size changes (important for text with spaces)
  useEffect(() => {
    if (!window.ResizeObserver) return;
    
    const selectedIndex = options.findIndex(option => option.value === selected);
    if (selectedIndex >= 0 && refs.current[selectedIndex]?.current) {
      const targetRef = refs.current[selectedIndex];
      
      const resizeObserver = new ResizeObserver(() => {
        updateHighlighterPosition();
      });
      
      if (targetRef.current) {
        resizeObserver.observe(targetRef.current);
      }
      
      return () => {
        if (targetRef.current) {
          resizeObserver.unobserve(targetRef.current);
        }
        resizeObserver.disconnect();
      };
    }
  }, [selected, options, updateHighlighterPosition]);
  
  // Use useLayoutEffect to ensure DOM measurements happen before paint
  useLayoutEffect(() => {
    updateHighlighterPosition();
  }, [updateHighlighterPosition]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      updateHighlighterPosition();
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateHighlighterPosition]);
  
  const handleSelect = (value: string) => {
    setSelected(value);
    onChange(value);
  };
  
  return (
    <div 
      ref={containerRef}
      className={`inline-flex items-center relative border rounded-md ${className}`}
      style={{ width: 'fit-content' }}
    >
      <div
        className="absolute h-full bg-blue-500 rounded z-0 transition-all duration-300 ease-in-out"
        style={highlighterStyle}
      />
      
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={refs.current[index]}
          className={`px-3 py-1 z-[0] rounded relative transition-colors duration-300 text-center ${
            selected === option.value ? "text-white font-medium" : "text-gray-700"
          } ${index > 0 ? "ml-1" : ""}`}
          onClick={() => handleSelect(option.value)}
          type="button"
        >
          {option.tooltip ? (
            <TooltippedElement
              message={option.tooltip}
              placement="top"
              element={<span>{option.label}</span>}
            />
          ) : (
            <span>{option.label}</span>
          )}
        </button>
      ))}
    </div>
  );
};

export default ToggleSwitch;